import React, { Component } from "react";
import KeyboardWrapper from "utils/keyboard";
import { getSectionState } from "utils/sectionState";
import Intro from "sections/intro";
import Progress from "sections/progress";
import MalariaMap from "sections/malariaMap";
import Heading from "components/heading";
import allStates from "data/states";
import { times } from "ramda";
import { interpolatePath } from "d3-interpolate-path";
import { interpolate } from "flubber";
import shuffle from "shuffle-array";
import {
  TweenLite,
  TweenMax,
  Power0,
  Power2,
  Power4,
  Expo,
  Elastic,
  Circ,
  SlowMo,
  TimelineMax,
} from "gsap";
import "./style.css";

if (!NodeList.forEach) {
  NodeList.forEach = Array.prototype.slice.call(this).forEach;
}

const getDataSetAttributes = node => {
  return Array.prototype.slice
    .call(node.attributes)
    .filter(({ name }) => name.indexOf("data-") > -1)
    .reduce(
      (obj, next) => ({ ...obj, [next.name.replace("data-", "")]: next.value }),
      {}
    );
};

class App extends Component {
  constructor(props) {
    super(props);
    this.state = { current: 0 };
    this.states = allStates;
    this.nextState = this.nextState.bind(this);
    this.prevState = this.prevState.bind(this);

    TweenLite.defaultEase = Power4.easeOut;
  }

  nextState() {
    this.tl.play();
  }

  prevState() {
    this.tl.pause().reverse();
  }

  startIntro() {
    const tl = new TimelineMax();
    tl.pause();
    const circles = this.$(".falling__circle");
    const circArray = shuffle(Array.prototype.slice.call(circles));
    const speed1 = circArray.slice(0, 4);
    const speed2 = circArray.slice(4, 7);
    const speed3 = circArray.slice(7, circles.length);

    const animation = {
      ease: SlowMo.ease.config(0.55, 0.55, false),
      y: 1180,
      repeat: -1,
    };

    const opacity = {
      ease: SlowMo.ease.config(0.55, 0.55, false),
      opacity: 0,
      yoyo: true,
      repeat: -1,
    };

    tl.staggerTo(speed1, 8.1, animation, 1.3, 0);
    tl.staggerFrom(speed1, 4.05, opacity, 1.3, 0);
    tl.staggerTo(speed2, 6.2, animation, 1.2, 0.1);
    tl.staggerFrom(speed2, 3.1, opacity, 1.2, 0);
    tl.staggerTo(speed3, 4.2, animation, 1.1, 0.2);
    tl.staggerFrom(speed3, 2.1, opacity, 1.1, 0);
    return tl;
  }

  fadeOutIntro(circles) {
    const tl = new TimelineMax();
    tl.pause();
    const wrapper = this.$("#intro");
    const intro = this.$("#intro .intro__fade");
    const allCircles = this.$("#intro .falling__circle");
    tl.staggerTo(intro, 1, { opacity: 0, y: -5 }, 0.1);
    tl.to(wrapper, 1, { opacity: 0 }, 1);
    tl.call(() => circles.pause());
    return tl;
  }

  fadeInIntro(circles) {
    const tl = new TimelineMax();
    tl.pause();
    const wrapper = this.$("#intro");
    const intro = this.$("#intro .intro__fade");
    const allCircles = this.$("#intro .falling__circle");
    tl.call(() => circles.play(), 0);
    tl.to(wrapper, 1, { opacity: 1 });
    tl.set(intro, { y: 5 }).staggerTo(intro, 1, { opacity: 1, y: 0 }, 0.2);
    return tl;
  }

  fadeInHeading(id) {
    const tl = new TimelineMax();
    tl.pause();
    const headingSelector = this.$(`#heading-${id} .heading__word`);
    tl.staggerFromTo(
      headingSelector,
      0.75,
      { opacity: 0, y: 20 },
      { opacity: 1, y: 0 },
      0.15
    );
    return tl;
  }

  fadeOutHeading(id) {
    const tl = new TimelineMax();
    tl.pause();
    const headingSelector = this.$(`#heading-${id} .heading__word`);
    tl.staggerTo(headingSelector, 0.75, { opacity: 0, y: -20 }, 0.15);
    return tl;
  }

  fadeInChart(id, fadeInLines = true) {
    const tl = new TimelineMax();
    tl.pause();

    // Grab everything!
    const { easeNone } = Power0;
    const ct = 2;
    const title = this.$(`#${id} .chart__title span`);
    const axisLines = this.$(`#ntds .chart__lines`);
    const axisNumbers = this.$(`#ntds .chart__axis-num`);
    const key = this.$(`#ntds .keys__fade`);
    const regressionKey = this.$(`#ntds .keys__key--regression`);
    const progressionKey = this.$(`#ntds .keys__key--progression`);
    const ticks = this.$(`#${id} .chart__tick`);
    const lines = this.$(`#${id} .chart__line`);
    const circles = this.$(`#${id} .circle`);
    const circleLine = this.$(`#${id} .circle-line`);
    const circleText = this.$(`#${id} .circle-text`);
    const goodCircles = this.$(`#${id} .good-circle`);
    const goodCircleLine = this.$(`#${id} .good-circle-line`);
    const goodCircleText = this.$(`#${id} .good-circle-text`);
    const badCircles = this.$(`#${id} .bad-circle`);
    const badCircleLine = this.$(`#${id} .bad-circle-line`);
    const badCircleText = this.$(`#${id} .bad-circle-text`);
    const line = this.$(`#${id} .chart__main-line`);
    const progress = this.$(`#${id} .chart__positive-mask`);
    const regress = this.$(`#${id} .chart__negative-mask`);
    const perYearTime = ct / lines.length;

    const fadeInWithLine = (selector, options) =>
      tl.staggerFrom(
        selector,
        perYearTime * 5,
        options,
        perYearTime,
        "start-line"
      );

    // Set initial
    tl.set(lines, { transformOrigin: "50% 100%" });
    tl.set(circleText, { transformOrigin: "50% 50%" });

    fadeInLines &&
      tl.from(axisLines, 0.3, { opacity: 0, scaleY: 0, ease: Power2.easeOut });

    // Fade in title
    tl.staggerFrom(title, 0.5, { opacity: 0, y: 15 }, 0.1, "-=0.1");

    // Fade in lines and ticks
    tl.staggerFrom(ticks, 1, { opacity: 0 }, 0.2, "-=0.75");

    // Animate in line
    tl.addLabel("start-line", "-=0.75");
    tl.to(line, ct, { "stroke-dashoffset": 0, ease: easeNone }, "start-line");
    fadeInWithLine(lines, { scale: 0 });
    fadeInLines && fadeInWithLine(axisNumbers, { opacity: 0 });

    // Fade in numbers along line
    circles.forEach((circle, i) => {
      const line = circle.querySelectorAll(".circle-line");
      const text = circle.querySelectorAll(".circle-text");
      const offset = `start-line+=${perYearTime * i}`;

      tl.from(circle, perYearTime * 5, { opacity: 0 }, offset);
      // tl.from(line, 0.3, { delay: 0.1, attr: { x2: 0, y2: -1 } }, offset);
      // tl.from(text, 0.3, { delay: 0.3, opacity: 0 }, offset);
    });

    tl.addLabel("start-others", "-=0.5");

    // Fade in progress
    const proTL = new TimelineMax().pause();
    proTL.from(progress, 1, { attr: { width: 0 } });
    proTL.from(goodCircles, 0.3, { opacity: 0 }, "-=0.3");
    proTL.from(goodCircleLine, 0.3, { attr: { x2: 0, y2: -1 } });
    proTL.from(goodCircleText, 0.3, { opacity: 0 });
    tl.add(proTL.play(), "start-others");

    // Fade in regression
    const regTL = new TimelineMax().pause();
    regTL.from(regress, 1, { attr: { width: 0 } }, "start-others");
    regTL.from(badCircles, 0.3, { opacity: 0 }, "-=0.3");
    regTL.from(badCircleLine, 0.3, { attr: { x2: 0, y2: -1 } });
    regTL.from(badCircleText, 0.3, { opacity: 0 });
    tl.add(regTL.play(), "start-others");

    // Fade in numbers along line
    circles.forEach((circle, i) => {
      const line = circle.querySelectorAll(".circle-line");
      const text = circle.querySelectorAll(".circle-text");
      tl.from(
        line,
        0.3,
        { delay: 0.1, attr: { x2: 0, y2: -1 } },
        "start-others+=1"
      );
      tl.from(text, 0.3, { delay: 0.3, opacity: 0 }, "start-others+=1");
    });

    fadeInLines && tl.staggerFrom(key, 1, { opacity: 0, x: -5 }, 0.2);
    return tl;
  }

  fadeOutChart(id, fadeOutLines = true) {
    const tl = new TimelineMax();
    tl.pause();

    const { easeNone } = Power0;
    const title = this.$(`#${id} .chart__title span`);
    const axisLines = this.$(`.chart__lines`);
    const axisNumbers = this.$(`.chart__axis-num`);
    const key = this.$(`#ntds .keys__fade`);
    const regressionKey = this.$(`#ntds .keys__key--regression`);
    const progressionKey = this.$(`#ntds .keys__key--progression`);
    const keys = [...key, ...regressionKey, ...progressionKey];
    const ticks = this.$(`#${id} .chart__tick`);
    const lines = this.$(`#${id} .chart__line`);
    const circles = this.$(`#${id} .circle`);
    const goodCircles = this.$(`#${id} .good-circle`);
    const badCircles = this.$(`#${id} .bad-circle`);
    const line = this.$(`#${id} .chart__main-line`);
    const progress = this.$(`#${id} .chart__positive`);
    const regress = this.$(`#${id} .chart__negative`);

    // Setup
    tl.set(lines, { transformOrigin: "50% 100%" });
    tl.set(circles, { transformOrigin: "50% 100%" });
    tl.set(badCircles, { transformOrigin: "50% 100%" });
    tl.set(goodCircles, { transformOrigin: "50% 100%" });
    tl.set(ticks, { transformOrigin: "50% 100%" });

    // Fade out chart furniture
    tl.to(progress, 0.5, { opacity: 0 }, 0);
    tl.to(regress, 0.5, { opacity: 0 }, 0);
    fadeOutLines && tl.to(keys, 0.5, { opacity: 0 }, 0);
    tl.to(circles, 0.5, { opacity: 0 }, 0);
    tl.to(badCircles, 0.5, { opacity: 0 }, 0);
    tl.to(goodCircles, 0.5, { opacity: 0 }, 0);
    tl.to(ticks, 0.5, { opacity: 0 }, 0);

    // Fade out lines
    tl.addLabel("start-lines");
    tl.to(lines, 1, { height: "0%", ease: Power4.easeIn });
    tl.to(
      line,
      1,
      {
        onUpdateParams: ["{self}", line[0]],
        onUpdate: (t, b) => {
          let origPath = b._origPath || b.getAttribute("d");
          let newPath = b._newPath || `M0,600 L1200,600`;
          b._origPath = origPath;
          b._newPath = newPath;

          const interpolator = interpolatePath(origPath, newPath);
          const progress = Power4.easeIn.getRatio(t.progress());
          b.setAttribute("d", interpolator(progress));
        },
      },
      "start-lines"
    );

    // Fade out everything else
    tl.addLabel("fade-all", "start-lines+=1");
    tl.to(line, 0.5, { opacity: 0 }, "fade-all");
    tl.staggerTo(title, 0.5, { opacity: 0, y: -15 }, 0.1, "start-lines");
    if (fadeOutLines) {
      tl.to(axisNumbers, 0.5, { opacity: 0 }, "fade-all");
      tl.to(axisLines, 0.5, { delay: 0, opacity: 0, scaleY: 0 }, "fade-all");
    }

    return tl;
  }

  fadeInMap() {
    const map = this.$(`#malaria .malaria__map`);
    const bg = this.$(`#malaria .malaria__background`);
    const year = this.$(`#malaria .year`);
    const tl = new TimelineMax();
    tl.pause();
    tl.set(map, { transformOrigin: "50% 50%" });

    // Fade in map
    tl.from(map, 1, { opacity: 0, scale: 0.9 });
    tl.from(bg, 1, { opacity: 0, scale: 0.9 }, 0);

    // Animate in year
    tl.from(year, 0.5, { opacity: 0 }, "-=0.2");

    return tl;
  }

  fadeOutMap() {
    const tl = new TimelineMax();
    tl.pause();
    const map = this.$(`#malaria .malaria__map`);
    const year = this.$(`#malaria .year`);
    const timeline = this.$(`#malaria .timeline`);
    const timelineTicks = this.$(`#malaria .timeline__tick`);
    const timelineYears = this.$(`#malaria .timeline__year`);
    const timelineMarker = this.$(`#malaria .timeline__marker`);
    const countries = this.$(`#malaria .country-with-malaria`);
    const bg = this.$(`#malaria .malaria__background`);

    // Fade out all
    tl.to(map, 1, { opacity: 0 });
    tl.to(bg, 1, { opacity: 0 }, 0);
    tl.to(year, 0.5, { opacity: 0 }, 0);
    tl.to(timelineYears, 0.2, { opacity: 0 }, 0);
    tl.staggerTo(timelineTicks, 0.2, { opacity: 0, scaleY: 0 }, 0.01, 0);
    tl.to(timelineMarker, 0.5, { opacity: 0, height: 0, delay: 0.2 }, 0);

    // Animate out timeline
    tl.to(timeline, 0.5, { scaleX: 0 }, 1);

    return tl;
  }

  goToYear(next, time = 5) {
    const tl = new TimelineMax();
    tl.pause();

    const { easeNone } = Power0;
    const year = this.$(`#malaria .year`);
    const timelineMarker = this.$(`#malaria .timeline__marker`);
    const countries = this.$(`#malaria .country-with-malaria`);
    const countryPaths = this.$(`#malaria .country-with-malaria path`);
    const timeline = this.$(`#malaria .timeline`);
    const ticks = this.$(`#malaria .timeline__tick`);
    const years = this.$(`#malaria .timeline__year`);
    const marker = this.$(`#malaria .timeline__marker`);
    const updateNum = (a, b) => (b[0].innerHTML = a.target.year);
    const getX = next => `${(next - 2015) / (2060 - 2015) * 100}%`;
    const startYear = { year: 2015 };
    tl.set(countryPaths, { transformOrigin: "50% 50%" });

    // Animate in timeline
    tl.from(timeline, 0.7, { scaleX: 0 });
    tl.staggerFrom(ticks, 0.2, { opacity: 0, scaleY: 0 }, 0.01);
    tl.from(years, 0.3, { opacity: 0 }, "-=0.1");
    tl.from(marker, 0.5, { opacity: 0, height: 0, delay: 0.2 }, "-=0.2");

    tl.addLabel("start", "+=1");

    // Update year
    tl.to(
      startYear,
      time,
      {
        ease: easeNone,
        year: next,
        roundProps: "year",
        onUpdateParams: ["{self}", year],
        onUpdate: updateNum,
      },
      "start"
    );

    // Update timeline
    tl.to(timelineMarker, time, { ease: easeNone, x: getX(next) }, "start");

    // Move countries
    countries.forEach((country, i) => {
      const { yearEnded, index, endX, endY } = getDataSetAttributes(country);
      const offset =
        time / (next - 2015) * (yearEnded - 2015) - 2.3 + index * 0.1;
      const countryTL = new TimelineMax();
      countryTL.pause();
      const path = country.querySelectorAll("path")[0];
      const circle = country.querySelectorAll("circle")[0];
      countryTL.to(path, 0.3, { opacity: 0, scale: 0.5 }, 0);
      countryTL.from(circle, 0.5, { opacity: 0 }, 0);
      countryTL.to(circle, 2, { attr: { cy: endY }, ease: Circ.easeIn }, 0.3);
      countryTL.to(circle, 2, { attr: { cx: endX }, ease: Circ.easeOut }, 0.3);

      tl.add(countryTL.play(), `start+=${Math.max(offset, 0)}`);
    });

    return tl;
  }

  delayGMO() {
    const countries = this.$(`#malaria .country-with-malaria`);
    const countryPaths = this.$(`#malaria .country-with-malaria path`);
    const { easeIn, easeOut } = Circ;

    // Setup
    const tl = new TimelineMax();
    tl.pause();
    tl.set(countryPaths, { transformOrigin: "50% 50%" });

    // Move countries to new position
    countries.forEach((country, i) => {
      const {
        yearEndedGMO,
        index,
        endGMOX,
        endGMOY,
        centerX,
        centerY,
      } = getDataSetAttributes(country);
      const countryTL = new TimelineMax().pause();
      const path = country.querySelectorAll("path")[0];
      const circle = country.querySelectorAll("circle")[0];
      const majorMove = yearEndedGMO > 2040;
      const time = majorMove ? 2 : 0.2;
      const x = majorMove ? centerX : endGMOX;
      const y = majorMove ? centerY : endGMOY;
      countryTL.to(circle, time, { attr: { cy: y }, ease: easeIn }, 0);
      countryTL.to(circle, time, { attr: { cx: x }, ease: easeOut }, 0);

      if (majorMove) {
        countryTL.to(circle, 0.5, { opacity: 0 }, time);
        countryTL.to(path, 0.2, { opacity: 1, scale: 1 }, time);
      }

      tl.add(countryTL.play(), `start+=${(countries.length - i) * 0.1}`);
    });

    return tl;
  }

  gotoGMOYear(next = 2060, time = 10) {
    const year = this.$(`#malaria .year`);
    const { easeNone } = Power0;
    const { easeIn, easeOut } = Circ;
    const marker = this.$(`#malaria .timeline__marker`);
    const countries = this.$(`#malaria .country-with-malaria`);
    const countryPaths = this.$(`#malaria .country-with-malaria path`);
    const updateNum = (a, b) => (b[0].innerHTML = a.target.year);
    const getX = next => `${(next - 2015) / (2060 - 2015) * 100}%`;
    const startYear = { year: 2040 };

    // Setup
    const tl = new TimelineMax();
    tl.pause();
    tl.set(countryPaths, { transformOrigin: "50% 50%" });
    tl.addLabel("start");

    // Update year and marker
    tl.to(
      startYear,
      time,
      {
        ease: easeNone,
        year: next,
        roundProps: "year",
        onUpdateParams: ["{self}", year],
        onUpdate: updateNum,
      },
      0
    );

    tl.to(marker, time, { ease: easeNone, x: getX(next) }, "start");

    // Move countries
    countries.forEach((country, i) => {
      const {
        yearEnded,
        endGMOX,
        endGMOY,
        yearEndedGMO,
        indexGMO,
      } = getDataSetAttributes(country);

      const majorMove = yearEndedGMO > 2040;
      const countryTL = new TimelineMax();
      countryTL.pause();
      const path = country.querySelectorAll("path")[0];
      const circle = country.querySelectorAll("circle")[0];
      const offset =
        time / (next - 2040) * (yearEndedGMO - 2040) - 2.3 + indexGMO * 0.1;

      if (majorMove) {
        countryTL.to(path, 0.2, { opacity: 0, scale: 0.5 }, 0);
        countryTL.to(circle, 0.5, { opacity: 1 }, 0);
        countryTL.to(circle, 2, { attr: { cy: endGMOY }, ease: easeIn }, 0.3);
        countryTL.to(circle, 2, { attr: { cx: endGMOX }, ease: easeOut }, 0.3);
        tl.add(countryTL.play(), `start+=${Math.max(offset, 0)}`);
      }
    });
    return tl;
  }

  showTools() {
    const tl = new TimelineMax();
    tl.pause();
    const wrapper = this.$(".tools");
    const year = this.$(`#malaria .year`);

    // Animate in tools
    tl.from(wrapper, 0.5, { opacity: 0 }, 0);
    tl.to(year, 0.5, { opacity: 0.1 }, 0);

    return tl;
  }

  showTool(i) {
    const tl = new TimelineMax();
    tl.pause();
    const tools = this.$(".tools__tool");

    tools.forEach((tool, j) => {
      if (j === i && i === 0) {
        const pills = Array.prototype.slice.call(this.$(".pill"));
        tl.set(pills, { transformOrigin: "50% 50%" });
        tl.staggerFrom(pills, 0.5, { y: -20, opacity: 0 }, 0.1, 0.25);
        tl.staggerFrom(
          pills.reverse().slice(1, pills.length),
          0.1,
          {
            ease: Power4.easeInOut,
            rotation: 10,
            yoyo: true,
            repeat: 3,
          },
          0.3
        );
      }

      if (j === i && i === 1) {
        const wing1 = this.$("#wing-1");
        const wing2 = this.$("#wing-2");
        tl.set(wing1, { transformOrigin: "100% 0%" });
        tl.set(wing2, { transformOrigin: "0% 0%" });
        tl.to(
          wing1,
          0.5,
          {
            rotation: -5,
            yoyo: true,
            ease: Power2.easeInOut,
            repeat: 3,
          },
          0.25
        );

        tl.to(
          wing2,
          0.5,
          {
            rotation: 5,
            yoyo: true,
            ease: Power2.easeInOut,
            repeat: 3,
          },
          0.25
        );
      }

      if (j === i && i === 2) {
        const scope = this.$("#microscope");
        const { easeInOut } = Power2;
        tl.set(scope, { transformOrigin: "50% 75%" });
        tl.to(
          scope,
          1,
          {
            rotation: -15,
            yoyo: true,
            ease: easeInOut,
            repeat: 1,
          },
          0.25
        );
        tl.to(scope, 1, {
          rotation: 10,
          yoyo: true,
          ease: easeInOut,
          repeat: 1,
        });
      }

      if (j === i && i === 3) {
        const bars = this.$(".charts-bar");
        const code = this.$(".code");
        const pulses = Array.prototype.slice.call(this.$(".pulses")).reverse();

        tl.set(pulses, { transformOrigin: "center center" });
        tl.staggerFrom(bars, 5 / bars.length, { opacity: 0 }, 0.1, 0.4);
        tl.staggerFrom(code, 5 / code.length, { opacity: 0 }, 0.05, 0.4);
        tl.staggerFrom(pulses, 0.5, { opacity: 0 }, 0.1, 1);
      }

      if (j === i) {
        const text = tool.querySelectorAll(".tools__title span");
        const image = tool.querySelectorAll(".tools__image");
        tl.from(image, 0.5, { opacity: 0 }, 0);
        tl.staggerFrom(text, 0.75, { opacity: 0, y: 15 }, 0.2, 0.25);
      }
    });

    return tl;
  }

  hideTools(offset = 0) {
    const tl = new TimelineMax();
    tl.pause();
    const wrapper = this.$(".tools");
    const tools = this.$(".tools__tool");
    const year = this.$(`#malaria .year`);

    // Hide tools
    tl.staggerTo(tools, 0.5, { opacity: 0, y: -20 }, 0.2);
    tl.to(wrapper, 0.5, { opacity: 0 });
    tl.to(year, 0.5, { opacity: 1 }, 0);
    return tl;
  }

  componentDidMount() {
    this.$ = elem => this.elem.querySelectorAll(elem);

    // Setup animation
    this.tl = new TimelineMax();
    this.tl.pause();

    const {
      startIntro,
      fadeInIntro,
      fadeOutIntro,
      fadeInHeading,
      fadeOutHeading,
      fadeInChart,
      fadeOutChart,
      fadeInMap,
      fadeOutMap,
      showTools,
      showTool,
      hideTools,
      goToYear,
      delayGMO,
      gotoGMOYear,
    } = this;

    const add = (tl, args = [], offset = 0, timeScale = 1) => {
      const allArgs = !(typeof args === "object") ? [args] : args;
      const timeline = tl.bind(this)(...allArgs);
      this.tl.add(timeline.play().timeScale(timeScale), `+=${offset}`);
    };
    const pause = () => this.tl.addPause();

    const introCircles = startIntro
      .bind(this)()
      .play();

    // Show NTD chart
    add(fadeOutIntro, [introCircles]);
    add(fadeInHeading, 0);
    add(fadeInChart, "ntds", -0.5);
    pause();

    // Show HIV chart
    add(fadeOutHeading, 0);
    add(fadeOutChart, ["ntds", false], -1);
    add(fadeInHeading, 1);
    add(fadeInChart, ["hiv", false], -0.5);
    pause();

    // Show Malaria chart
    add(fadeOutHeading, 1);
    add(fadeOutChart, ["hiv", false], -0.5);
    add(fadeInHeading, 2);
    add(fadeInChart, ["malaria", false], -0.5);
    pause();

    // Show map
    add(fadeOutChart, "malaria", 0);
    add(fadeOutHeading, 2, -0.5);
    add(fadeInHeading, 3, 0);
    add(fadeInMap, -0.5);
    pause();

    // Show tools
    add(fadeOutHeading, 3, 0);
    add(fadeInHeading, 4, 0);
    add(showTools);
    add(showTool, 0);
    pause();
    add(showTool, 1);
    pause();
    add(showTool, 2);
    pause();
    add(showTool, 3);
    pause();

    // Go to year 2040
    add(fadeOutHeading, 4, 0);
    add(hideTools, 0);
    add(fadeInHeading, 5, 0);
    add(goToYear, [2040, 20], 0);
    pause();

    // Delay GMO
    add(fadeOutHeading, 5, 0);
    add(fadeInHeading, 6, 0);
    add(delayGMO, 0);
    pause();

    // Go to 2060 without GMO
    add(fadeOutHeading, 6, 0);
    add(fadeInHeading, 7, 0);
    add(gotoGMOYear, 2060);
    pause();

    // Go back to intro
    add(fadeOutMap, 0);
    add(fadeOutHeading, 7, -0.2);
    add(fadeInIntro, [introCircles]);
  }

  render() {
    return (
      <div className="app-wrapper" ref={elem => (this.elem = elem)}>
        <KeyboardWrapper onLeft={this.prevState} onRight={this.nextState}>
          <Heading headings={allStates.headings} />
          <Intro {...allStates.intro} />
          <Progress />
          <MalariaMap />
        </KeyboardWrapper>
      </div>
    );
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/app.js